import { FC, useState } from 'react';
import { Delete, Visibility } from '@mui/icons-material';
import { UnrealisedFXGainOrLossSubTypeLabels } from 'further-ui/labels';
import getUnrealisedFxDisplayAmount from './getUnrealisedFxDisplayAmount';
import { numberToCurrencyString } from 'further-ui/utils';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { usePagination } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import TypographyButton from 'further-ui/components/TypographyButton';
import { SortOrder } from 'further-types/utils';

const pageId = 'unrealised-fx-history';

type Props = {
  vehicleId: string;
  periodId?: string;
  onViewRecord?: (recordId: string) => void;
};

const PayablesOrReceivablesHistory: FC<Props> = ({
  vehicleId,
  periodId,
  onViewRecord,
}) => {
  const { vehicle } = useLayoutContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recordIdToDelete, setRecordIdToDelete] = useState<string | null>(null);

  const pagination = usePagination({
    id: pageId,
    order: SortOrder.Desc,
    orderBy: 'createdAt',
  });

  const { data, isLoading, refetch } =
    api.unrealisedFxGainOrLoss.listPosted.useQuery({
      vehicleId,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      order: pagination.order,
      orderBy: pagination.orderBy,
      excludePeriodId: periodId,
    });

  const mutationOpts = useHandleTrpcMutation();
  const { mutate: deleteRecord, isPending: isDeleting } =
    api.unrealisedFxGainOrLoss.delete.useMutation(
      mutationOpts({
        successMessage: 'Unrealised FX gain/loss deleted successfully.',
        refetchType: 'active',
        onSuccess: () => {
          refetch();
          setDeleteDialogOpen(false);
          setRecordIdToDelete(null);
        },
      }),
    );

  const handleOpenDeleteDialog = (recordId: string) => {
    setRecordIdToDelete(recordId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRecordIdToDelete(null);
  };

  const handleDelete = () => {
    if (recordIdToDelete) {
      deleteRecord({
        vehicleId,
        recordId: recordIdToDelete,
      });
    }
  };

  const rows = data?.results ?? [];

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'ledgerDate',
      label: 'Ledger Date',
      render: (row) => row.ledgerDate.toLabel(),
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      render: (row) => row.counterpartyLabel ?? '-',
      disableSorting: true,
    },
    {
      key: 'gainOrLossSubType',
      label: 'Type',
      render: (row) =>
        UnrealisedFXGainOrLossSubTypeLabels[row.gainOrLossSubType],
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount (LCY)',
      render: (row) =>
        numberToCurrencyString(
          getUnrealisedFxDisplayAmount(row.gainOrLossSubType, row.amountLcy),
          {
            fallback: '-',
            currency: vehicle?.baseCurrency,
            negativeInBrackets: true,
          },
        ),
      disableSorting: true,
    },
  ];

  if (!vehicle || (!isLoading && !data?.results?.length)) {
    return null;
  }

  return (
    <Column spacing="lg">
      <TypographyButton
        text="History of posted unrealised FX gains/losses"
        textDecoration="none"
        onClick={() => setIsExpanded((prev) => !prev)}
        showExpandableStartAdornment
        isExpanded={isExpanded}
      />
      {isExpanded && (
        <Table
          actions={[
            {
              label: 'View',
              icon: Visibility,
              onClick: onViewRecord ? (row) => onViewRecord(row.id) : undefined,
            },
            {
              label: 'Delete',
              icon: Delete,
              disabled: (row) => !row.canDelete,
              tooltip: (row) =>
                row.canDelete ? undefined : row.deleteReason ?? 'Cannot delete',
              onClick: (row) => handleOpenDeleteDialog(row.id),
            },
          ]}
          columns={columns}
          rows={rows}
          isLoading={isLoading}
          pagination={{
            page: pagination.page,
            rowsPerPage: pagination.rowsPerPage,
            order: pagination.order,
            orderBy: pagination.orderBy,
            handleRequestSort: pagination.handleRequestSort,
            handleChangePage: pagination.handleChangePage,
            handleChangeRowsPerPage: pagination.handleChangeRowsPerPage,
          }}
          rowsTotalCount={data?.totalCount ?? 0}
        />
      )}
      <ConfirmDialog
        onClose={handleCloseDeleteDialog}
        open={deleteDialogOpen}
        title="Delete unrealised FX gain/loss?"
        loading={isDeleting}
        content="Are you sure you want to delete this unrealised FX gain/loss? This cannot be undone."
        actions={{
          confirm: {
            onClick: handleDelete,
            label: 'Delete',
          },
        }}
      />
    </Column>
  );
};

export default PayablesOrReceivablesHistory;
